import React, { useState, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Mic, MicOff, Loader2, X } from 'lucide-react';
import { useAuth, API } from '../App';
import axios from 'axios';

/**
 * VoiceInput - Premium voice-to-text input component
 * Simple, stable, and frictionless voice recording with transcription
 */
const VoiceInput = ({ 
  onTranscription, 
  placeholder = "Appuie pour parler...",
  className = "",
  disabled = false 
}) => {
  const { token } = useAuth();
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);

  const startRecording = useCallback(async () => {
    setError(null);
    
    try {
      // Request microphone access
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 44100
        }
      });

      // Create MediaRecorder with webm format (widely supported)
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'audio/webm;codecs=opus'
      });

      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        // Stop all tracks
        stream.getTracks().forEach(track => track.stop());
        
        // Create blob from chunks
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        
        // Send for transcription
        await transcribeAudio(audioBlob);
      };

      // Start recording
      mediaRecorder.start(100); // Collect data every 100ms
      setIsRecording(true);
      
    } catch (err) {
      console.error('Error starting recording:', err);
      if (err.name === 'NotAllowedError') {
        setError("Accès au microphone refusé. Autorise l'accès dans les paramètres de ton navigateur.");
      } else if (err.name === 'NotFoundError') {
        setError("Aucun microphone détecté.");
      } else {
        setError("Impossible de démarrer l'enregistrement.");
      }
    }
  }, []);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  }, [isRecording]);

  const transcribeAudio = async (audioBlob) => {
    setIsProcessing(true);
    setError(null);

    try {
      // Create form data
      const formData = new FormData();
      formData.append('audio', audioBlob, 'recording.webm');

      // Send to backend for transcription
      const response = await axios.post(`${API}/voice/transcribe`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        },
        timeout: 30000 // 30 second timeout
      });

      if (response.data.text) {
        onTranscription(response.data.text);
      } else {
        setError("Aucun texte détecté. Réessaie en parlant plus clairement.");
      }
      
    } catch (err) {
      console.error('Transcription error:', err);
      if (err.response?.status === 413) {
        setError("Enregistrement trop long. Limite : 60 secondes.");
      } else if (err.code === 'ECONNABORTED') {
        setError("Délai dépassé. Réessaie avec un enregistrement plus court.");
      } else {
        setError("Erreur de transcription. Réessaie.");
      }
    } finally {
      setIsProcessing(false);
    }
  };

  const handleClick = () => {
    if (disabled || isProcessing) return;
    
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  };

  return (
    <div className={`relative inline-flex items-center gap-2 ${className}`}>
      {/* Main microphone button */}
      <motion.button
        type="button"
        onClick={handleClick}
        disabled={disabled || isProcessing}
        whileHover={{ scale: disabled ? 1 : 1.05 }}
        whileTap={{ scale: disabled ? 1 : 0.95 }}
        className={`
          relative p-3 rounded-xl transition-all duration-200
          ${isRecording 
            ? 'bg-red-500/20 border-red-500/50 text-red-400' 
            : isProcessing 
              ? 'bg-[#D4AF37]/10 border-[#D4AF37]/30 text-[#D4AF37]'
              : 'bg-[#1C1C1C] border-[#27272A] text-[#71717A] hover:text-white hover:border-[#D4AF37]/50'
          }
          border disabled:opacity-50 disabled:cursor-not-allowed
        `}
        data-testid="voice-input-button"
        aria-label={isRecording ? "Arrêter l'enregistrement" : "Commencer l'enregistrement"}
      >
        {/* Recording pulse animation */}
        {isRecording && (
          <motion.span
            initial={{ scale: 1, opacity: 0.5 }}
            animate={{ scale: 1.5, opacity: 0 }}
            transition={{ duration: 1, repeat: Infinity }}
            className="absolute inset-0 bg-red-500 rounded-xl"
          />
        )}
        
        <span className="relative z-10">
          {isProcessing ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : isRecording ? (
            <MicOff className="w-5 h-5" />
          ) : (
            <Mic className="w-5 h-5" />
          )}
        </span>
      </motion.button>

      {/* Status indicator */}
      <AnimatePresence>
        {(isRecording || isProcessing) && (
          <motion.span
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -10 }}
            className={`text-sm ${isRecording ? 'text-red-400' : 'text-[#D4AF37]'}`}
          >
            {isRecording ? "Parle maintenant..." : "Transcription..."}
          </motion.span>
        )}
      </AnimatePresence>

      {/* Error message */}
      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            className="absolute left-0 top-full mt-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-xs max-w-xs z-10"
          >
            <button 
              onClick={() => setError(null)}
              className="absolute top-1 right-1 p-1 hover:bg-red-500/20 rounded"
            >
              <X className="w-3 h-3" />
            </button>
            {error}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

/**
 * VoiceTextArea - Textarea with integrated voice input
 * Combines text input with voice recording capability
 */
export const VoiceTextArea = ({
  value,
  onChange,
  placeholder = "Écris ou parle ta réponse...",
  rows = 4,
  className = "",
  disabled = false,
  maxLength
}) => {
  const handleTranscription = (text) => {
    // Append transcribed text to existing value
    const newValue = value ? `${value} ${text}` : text;
    onChange(newValue);
  };

  return (
    <div className={`relative ${className}`}>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={rows}
        maxLength={maxLength}
        disabled={disabled}
        className="w-full p-4 pr-16 bg-[#111] border border-[#27272A] rounded-xl text-white placeholder-[#52525B] focus:border-[#D4AF37]/50 focus:outline-none focus:ring-1 focus:ring-[#D4AF37]/20 resize-none transition-colors"
        data-testid="voice-textarea"
      />
      
      {/* Voice input button positioned inside textarea */}
      <div className="absolute right-3 bottom-3">
        <VoiceInput 
          onTranscription={handleTranscription}
          disabled={disabled}
        />
      </div>
      
      {/* Character count if maxLength provided */}
      {maxLength && (
        <span className="absolute right-3 top-3 text-xs text-[#52525B]">
          {value?.length || 0}/{maxLength}
        </span>
      )}
    </div>
  );
};

/**
 * VoiceInputField - Input field with voice capability
 * For shorter single-line inputs
 */
export const VoiceInputField = ({
  value,
  onChange,
  placeholder = "Écris ou parle...",
  className = "",
  disabled = false
}) => {
  const handleTranscription = (text) => {
    const newValue = value ? `${value} ${text}` : text;
    onChange(newValue);
  };

  return (
    <div className={`relative ${className}`}>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        className="w-full p-4 pr-14 bg-[#111] border border-[#27272A] rounded-xl text-white placeholder-[#52525B] focus:border-[#D4AF37]/50 focus:outline-none focus:ring-1 focus:ring-[#D4AF37]/20 transition-colors"
        data-testid="voice-input-field"
      />
      
      <div className="absolute right-2 top-1/2 -translate-y-1/2">
        <VoiceInput 
          onTranscription={handleTranscription}
          disabled={disabled}
          className="!p-2"
        />
      </div>
    </div>
  );
};

export default VoiceInput;
